import { useCallback, useEffect, useState } from 'react';
import { Loader2, Sparkles, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { formatNXC } from '../lib/formatNXC';

export default function ExplainBalanceSheet({ open, groupId, member, onClose }) {
  const [data, setData] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async (ai = 0) => {
    const r = await api.get(`/groups/${groupId}/members/${member.id}/balance-explain`, { params: { ai } });
    setData(r.data);
  }, [groupId, member?.id]);

  useEffect(() => {
    if (!open || !member?.id) return;
    setData(null);
    load(0).catch(() => onClose?.());
  }, [open, member?.id, load, onClose]);

  const askAi = async () => {
    setAiBusy(true);
    try { await load(1); } finally { setAiBusy(false); }
  };

  const tone = data?.direction === 'to_get' ? 'text-emerald-600 dark:text-emerald-400'
    : data?.direction === 'to_pay' ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground';

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[88vh] overflow-y-auto" data-testid="explain-balance-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl">Where this number comes from</SheetTitle>
          <SheetDescription>{member?.name} · {data?.group_name || ''}</SheetDescription>
        </SheetHeader>

        {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10" /> : (
          <div className="mt-3 pb-8 space-y-4">
            <div className="rounded-2xl border p-4">
              <p className={`font-display font-black text-3xl tabular-nums ${tone}`} data-testid="explain-headline">
                {data.direction === 'settled' ? 'All settled' : `${data.direction === 'to_get' ? 'To get' : 'To pay'} ${formatNXC(Math.abs(data.balance))}`}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{data.entry_count} ledger entries counted</p>
            </div>

            <div className="space-y-2" data-testid="explain-buckets">
              {data.buckets.map((b) => (
                <div key={b.label} className="flex items-start gap-3 rounded-2xl border p-3" data-testid={`explain-bucket-${b.label.replace(/\s+/g, '-').toLowerCase()}`}>
                  {b.amount < 0
                    ? <ArrowDownRight className="w-4 h-4 mt-0.5 text-rose-500 shrink-0" />
                    : <ArrowUpRight className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{b.label}</p>
                    <p className="text-xs text-muted-foreground">{b.hint} · {b.count} {b.count === 1 ? 'entry' : 'entries'}</p>
                  </div>
                  <p className={`text-sm font-bold tabular-nums shrink-0 ${b.amount < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                    {b.amount < 0 ? '−' : '+'}{formatNXC(Math.abs(b.amount))}
                  </p>
                </div>
              ))}
            </div>

            {data.pending_guest_fees_total > 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="explain-pending-guest">
                Plus {formatNXC(data.pending_guest_fees_total)} of guest fees from before they joined — not in the running balance yet.
              </p>
            )}

            {data.recent.length > 0 && (
              <div className="rounded-2xl border p-3" data-testid="explain-recent">
                <p className="font-display text-xs font-bold uppercase tracking-[0.16em] mb-2">Latest activity</p>
                <div className="divide-y divide-border/60">
                  {data.recent.map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-1.5">
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{r.label}</p>
                        <p className="text-xs text-muted-foreground">{(r.date || '').slice(0, 10)}</p>
                      </div>
                      <p className={`text-xs font-bold tabular-nums shrink-0 ${r.delta < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {r.delta < 0 ? '−' : '+'}{formatNXC(Math.abs(r.delta))}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.ai_summary ? (
              <div className="rounded-2xl border bg-muted/40 p-3" data-testid="explain-ai-summary">
                <p className="text-xs font-bold uppercase tracking-[0.16em] mb-1 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" /> In plain English
                </p>
                <p className="text-sm leading-relaxed">{data.ai_summary}</p>
              </div>
            ) : (
              <Button variant="outline" className="rounded-full w-full h-10" onClick={askAi} disabled={aiBusy} data-testid="explain-ai-btn">
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Explain in plain English</>}
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
